/**
 * SWS Authentication Provider
 * Handles authentication logic specific to the SWS API system
 */

import { User } from '../../types';
import { IAuthProvider, LoginResponse, PermissionData } from './IAuthProvider';
import { storageService } from '../storageService';
import { configService } from '../configService';
import { trackApiCall, setSentryUser, captureExceptionWithContext, getSentryLogger } from '../../utils/sentryHelpers';

export class SwsAuthProvider implements IAuthProvider {
  /**
   * Authenticate with SWS API
   */
  async login(username: string, password: string, companyCode?: string): Promise<LoginResponse> {
    const logger = getSentryLogger();

    // Company code is required for SWS login
    if (!companyCode || !companyCode.trim()) {
      return {
        success: false,
        error: 'Company code is required for SWS authentication'
      };
    }

    const client = companyCode.toLowerCase();
    const params = new URLSearchParams({
      client: client,
      userCode: username,
      password: password
    });

    const loginUrl = `${configService.getSwsApiUrl(configService.getConfig().swsApi.loginPath)}?${params}`;

    return trackApiCall(loginUrl, 'GET', async () => {
      try {
        const response = await fetch(loginUrl, {
          method: 'GET',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          logger.error(`SWS API HTTP Error (${client})`, {
            status: response.status,
            statusText: response.statusText,
            url: response.url
          });
          return {
            success: false,
            error: `SWS authentication failed for company '${companyCode}': ${response.statusText}`
          };
        }

        const data = await response.json();

        if (data.code === 200 && data.data) {
          const userData = data.data;
          const accessToken = userData['access-token'] || userData.access_token;
          const appId = userData.appId;

          // Fetch SWS menu permissions
          const menuPermissions = await this.fetchSWSPermissions(accessToken, appId);

          // Check if user has required permissions
          const hasRequiredPermissions = this.checkPermissions(menuPermissions);
          if (!hasRequiredPermissions) {
            return {
              success: false,
              error: 'Access denied: You do not have the required permissions to access this application.'
            };
          }

          // Store SWS-specific tokens
          storageService.setItem('swsAccessToken', accessToken);
          storageService.setItem('swsAppId', appId);
          storageService.setItem('authToken', accessToken); // Fallback

          // Store SWS menu permissions
          storageService.setItem('swsMenuPermissions', menuPermissions);

          const user: User = {
            id: userData.id?.toString() || 'sws-user',
            username: username,
            password: '', // Never store password
            email: userData.email || `${username}@${client}.com`,
            firstName: userData.firstName || userData.name || username,
            lastName: userData.lastName || 'User',
            role: 'admin', // Default role, will be overridden by actual permissions
            department: userData.company || companyCode.toUpperCase(),
            phone: userData.phone || '',
            lastLogin: new Date().toISOString(),
            isActive: true,
            system: 'sws' as const,
            company: userData.company || companyCode.toUpperCase(),
            rawPermissionData: { menuPermissions } // Store menu permissions for later use
          } as User & { rawPermissionData: any };

          // Set Sentry user context
          setSentryUser({
            id: user.id,
            username: user.username,
            email: user.email,
            companyCode: companyCode,
            apiSystem: 'sws'
          });

          return {
            success: true,
            user,
            token: accessToken
          };
        }

        // Check for specific error codes and use API message when available
        if (data.code === 10005 && data.msg) {
          // Invalid credentials error
          return {
            success: false,
            error: data.msg // Use the API's error message
          };
        }

        // For other error codes, include the API message if available
        if (data.msg) {
          return {
            success: false,
            error: data.msg
          };
        }

        // Fallback to generic message if no specific message from API
        return {
          success: false,
          error: `Authentication failed for company '${companyCode}'. Please check your credentials and company code.`
        };
      } catch (error: any) {
        logger.error("SWS API login error", { error: error.message });
        captureExceptionWithContext(error, {
          tags: { auth_system: 'sws', component: 'SwsAuthProvider' },
          extra: { username, companyCode }
        });

        // Preserve error message if available
        if (error.message) {
          return {
            success: false,
            error: `SWS authentication error: ${error.message}`
          };
        }

        return {
          success: false,
          error: 'SWS API connection failed. Please check your network connection and try again.'
        };
      }
    });
  }

  /**
   * Logout from SWS API
   */
  async logout(token: string): Promise<void> {
    try {
      // Ensure config is loaded before attempting to get API URL
      await configService.loadConfig();

      const appId = storageService.getItem<string>('swsAppId') || 'wms_estis';

      const response = await fetch(configService.getSwsApiUrl('/logout'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'access-token': token,
          'appId': appId,
          'appToken': 'SmartBoxMigrateData',
          'appSecret': '23f0c46e560e50ac945df1aa22945df1a'
        }
      });

      if (!response.ok) {
        throw new Error(`SWS logout HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
    } catch (error: any) {
      console.error('SWS API logout failed:', error);
      throw error;
    }
  }

  /**
   * Validate SWS API token by making a test request
   */
  async validateToken(token: string): Promise<boolean> {
    try {
      // Ensure config is loaded before attempting to get API URL
      await configService.loadConfig();

      const appId = storageService.getItem<string>('swsAppId') || 'wms_estis';
      const response = await fetch(configService.getSwsApiUrl('/userInfo'), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'access-token': token,
          'appId': appId,
          'appToken': 'SmartBoxMigrateData',
          'appSecret': '23f0c46e560e50ac945df1aa22945df1a'
        }
      });

      const data = await response.json();
      return data.code === 200; // SWS API success code
    } catch {
      return false;
    }
  }

  /**
   * Get system type identifier
   */
  getSystemType(): 'sws' {
    return 'sws';
  }

  /**
   * Check if user has required SWS permissions
   * Reason: Verifies that user has at least one of the required mobile or web permissions
   */
  checkPermissions(menuPermissions: { mobile: any[], web: any[] }): boolean {
    try {
      const { mobile, web } = menuPermissions;

      // Flatten mobile menu structure to find all items
      const allMobileItems: any[] = [];
      const flattenMenu = (items: any[]) => {
        items.forEach(item => {
          allMobileItems.push(item);
          if (item.children && Array.isArray(item.children)) {
            flattenMenu(item.children);
          }
        });
      };

      if (Array.isArray(mobile)) {
        flattenMenu(mobile);
      }

      // Check mobile permissions
      const hasTagItems = allMobileItems.some(item => item.menuCode === 'tag_items');
      const hasManageTags = allMobileItems.some(item => item.menuCode === 'manage_tags');

      // Check web permissions for md_tags with actions
      const mdTagsItem = Array.isArray(web) ?
        web.find(item => item.menuCode === 'md_tags') : null;
      const hasUpdateQuantity = mdTagsItem?.action?.some((a: any) => a.actionCode === 'edit') || false;
      const hasClearTagData = mdTagsItem?.action?.some((a: any) => a.actionCode === 'delete') || false;

      console.log('🔐 SWS Permission Check:', {
        hasTagItems,
        hasManageTags,
        hasUpdateQuantity,
        hasClearTagData,
        mobileMenuCodes: allMobileItems.map(item => item.menuCode),
        webMenuWithActions: mdTagsItem ? {
          menuCode: mdTagsItem.menuCode,
          actions: mdTagsItem.action?.map((a: any) => a.actionCode)
        } : null
      });

      // User needs at least one permission to access the PWA
      return hasTagItems || hasManageTags || hasUpdateQuantity || hasClearTagData;
    } catch (error) {
      console.error('Error checking SWS permissions:', error);
      return false;
    }
  }

  /**
   * Fetch SWS menu permissions (both mobile and web)
   * Reason: Retrieves complete permission data needed for authorization decisions
   */
  private async fetchSWSPermissions(accessToken: string, appId: string): Promise<{ mobile: any[], web: any[] }> {
    try {
      // Fetch mobile permissions (type=2)
      const mobileResponse = await fetch(
        `${configService.getSwsApiUrl('/authMenuList')}?type=2&orderBy=asc&field=sortNum`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'access-token': accessToken,
            'appId': appId,
            'appToken': 'SmartBoxMigrateData',
            'appSecret': '23f0c46e560e50ac945df1aa22945df1a'
          }
        }
      );

      // Fetch web permissions (type=1)
      const webResponse = await fetch(
        `${configService.getSwsApiUrl('/authMenuList')}?field=sortNum&orderBy=asc&type=1`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'access-token': accessToken,
            'appId': appId,
            'appToken': 'SmartBoxMigrateData',
            'appSecret': '23f0c46e560e50ac945df1aa22945df1a'
          }
        }
      );

      const mobileData = await mobileResponse.json();
      const webData = await webResponse.json();

      console.log('📱 SWS Mobile Permissions:', mobileData);
      console.log('💻 SWS Web Permissions:', webData);

      return {
        mobile: mobileData.data || [],
        web: webData.data || []
      };
    } catch (error) {
      console.error('Failed to fetch SWS permissions:', error);
      // Return empty arrays if fetch fails
      return { mobile: [], web: [] };
    }
  }
}
